// src/contexts/AuthContext.js

import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import useUserService from '../services/UserService'

export const AuthContext = createContext()

const LAST_PROCESSED_LOGIN_KEY = 'ivetted.lastProcessedLoginKey'

const getAccountKey = (account) => {
  if (!account) {
    return null
  }

  return account.homeAccountId || account.localAccountId || account.username || null
}

const getLoginProcessKey = (account) => {
  if (!account) {
    return null
  }

  const accountKey = getAccountKey(account)

  if (!accountKey) {
    return null
  }

  const claims = account.idTokenClaims || {}

  /*
   * iat/auth_time should change on a new login-issued token.
   * This prevents process() running on every route/page load while still
   * allowing it to run again after a genuine new login.
   */
  const issuedAt = claims.iat || claims.auth_time || claims.nbf || claims.exp || 'no-token-time'
  const policy = claims.tfp || claims.acr || 'no-policy'

  return `${accountKey}:${policy}:${issuedAt}`
}

export const AuthProvider = ({ children, msalInstance, activeAccount }) => {
  const { instance, accounts, inProgress } = useMsal()
  const { process: processUser } = useUserService()

  const [state, setState] = useState({
    isProcessing: false,
    processError: null,
    processedLoginKey: null,
  })

  const processingRef = useRef(false)

  const getCurrentAccount = () => {
    return (
      activeAccount ||
      instance.getActiveAccount() ||
      accounts[0] ||
      instance.getAllAccounts()[0] ||
      msalInstance?.getActiveAccount?.() ||
      msalInstance?.getAllAccounts?.()[0] ||
      null
    )
  }

  useEffect(() => {
    const runProcessForNewLoginToken = async () => {
      if (inProgress !== InteractionStatus.None) {
        return
      }

      const account = getCurrentAccount()

      if (!account) {
        return
      }

      instance.setActiveAccount(account)

      const loginProcessKey = getLoginProcessKey(account)

      if (!loginProcessKey) {
        return
      }

      const lastProcessedLoginKey = localStorage.getItem(LAST_PROCESSED_LOGIN_KEY)

      /*
       * This is the main guard:
       * same cached login token = do not call process again.
       */
      if (lastProcessedLoginKey === loginProcessKey) {
        return
      }

      if (processingRef.current) {
        return
      }

      processingRef.current = true

      setState((prevState) => ({
        ...prevState,
        isProcessing: true,
        processError: null,
      }))

      try {
        await processUser()

        localStorage.setItem(LAST_PROCESSED_LOGIN_KEY, loginProcessKey)

        setState((prevState) => ({
          ...prevState,
          isProcessing: false,
          processError: null,
          processedLoginKey: loginProcessKey,
        }))
      } catch (error) {
        console.error('AuthContext process failed', error)

        /*
         * Do not mark the login as processed if the endpoint failed.
         * That means a refresh/new login can retry it.
         */
        setState((prevState) => ({
          ...prevState,
          isProcessing: false,
          processError: error,
          processedLoginKey: null,
        }))
      } finally {
        processingRef.current = false
      }
    }

    runProcessForNewLoginToken()

    // Do not add processUser unless useUserService memoizes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, inProgress, activeAccount, msalInstance])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        activeAccount: getCurrentAccount(),
        msalInstance: msalInstance || instance,
        process: processUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node,
  msalInstance: PropTypes.object,
  activeAccount: PropTypes.object,
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
